import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Grid, Image, Skeleton, Tabs, TextInput } from '@mantine/core';
import PropTypes from 'prop-types';
import { AlertCircle, Photo } from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import CreationWizardButton from './CreationWizardButton';
import AppText from '../../common/AppText';
import AppCard from '../../common/AppCard';
import AppFlexbox from '../../common/AppFlexbox';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import PaginationList from '../../common/PaginationList';
import * as classes from '../../../styles/nestedStyles.module.css';
import AppCheckbox from '../../common/AppCheckbox';

const StoreCreationSelectProductsStep = ({
  loading,
  createOptions,
  formState,
  setFormState
}) => {
  const hasFetched = useRef(false);
  const { state, fetchStoreStockContentOptions } = useContext(StoreContext);
  const [filterState, setFilterState] = useState({
    activeTab: 'stock',
    search: '',
    pageIndex: 1
  });
  const customProducts = createOptions?.ecomExternalProducts || [];
  const selectedStockContent =
    createOptions?.ecomStockContent.filter((sc) =>
      formState.ecomStockContent.some(
        (c) => c.pkEcomStockContent === sc.pkEcomStockContent
      )
    ) || [];

  const productsLoading =
    !hasFetched.current || state.ecomStockContentOptions.loading;
  const ecomStockContentOptions = state.ecomStockContentOptions.value.filter(
    (s) =>
      formState.ecomStockContent.some(
        (c) => c.pkEcomStockContent === s.pkEcomStockContent
      )
  );
  const stockContentProducts = ecomStockContentOptions.reduce((r, c) => {
    c.ecomStore.ecomStoreProducts.forEach((p) => {
      const existing = r.find(
        (rp) => rp.fkEcomVendorProduct === p.fkEcomVendorProduct
      );
      if (existing) {
        existing.pkEcomStockContent.push(c.pkEcomStockContent);
      }
      else {
        r.push({
          ...p,
          pkEcomStockContent: [c.pkEcomStockContent]
        });
      }
    });
    return r;
  }, []);

  const filteredProducts = (filterState.activeTab === 'custom'
    ? customProducts
    : stockContentProducts
  ).filter(
    (t) =>
      !filterState.search ||
      t.name.toLowerCase().includes(filterState.search.toLowerCase())
  );

  const allProductsSelected =
    filterState.activeTab === 'custom'
      ? filteredProducts.every((p) =>
          formState.customProducts.pkEcomExternalProducts.includes(
            p.pkEcomExternalProduct
          )
        )
      : filteredProducts.every((p) =>
          formState.pkEcomVendorProducts.includes(p.fkEcomVendorProduct)
        );

  useEffect(() => {
    if (formState.selectedProductsOption === 'select') {
      if (
        selectedStockContent.length > 0 &&
        ecomStockContentOptions.length < formState.ecomStockContent.length &&
        !state.ecomStockContentOptions.loading
      ) {
        fetchStoreStockContentOptions(
          {
            pkEcomStockContent: formState.ecomStockContent.map(
              (sc) => sc.pkEcomStockContent
            )
          },
          null,
          triggerNotification
        );
      }

      hasFetched.current = true;
    }
  }, [formState.selectedProductsOption]);

  return (
    <AppStack style={{ gap: 10, paddingTop: 10 }}>
      <Grid>
        <Grid.Col span={6}>
          <CreationWizardButton
            description="Use all the products from stock content"
            label="All products"
            onClick={() => {
              setFormState({
                ...formState,
                selectedProductsOption: 'all'
              });
            }}
            selected={formState.selectedProductsOption === 'all'}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <CreationWizardButton
            description="Choose specific products"
            label="Select products"
            onClick={() => {
              setFormState({
                ...formState,
                selectedProductsOption: 'select'
              });
            }}
            selected={formState.selectedProductsOption === 'select'}
          />
        </Grid.Col>
      </Grid>

      {formState.selectedProductsOption === 'select' && (
        <AppStack>
          <AppStack
            style={{
              flex: 1,
              gap: 10,
              overflow: 'hidden',
              height: '100%'
            }}
          >
            <AppStack style={{ gap: 0 }}>
              <AppStack style={{ gap: 5 }}>
                <Tabs
                  onChange={(activeTab) => {
                    setFilterState({ ...filterState, activeTab, pageIndex: 1 });
                  }}
                  value={filterState.activeTab}
                >
                  <Tabs.List>
                    {productsLoading ? (
                      <>
                        <Tabs.Tab color="dark" value="stock">
                          <Skeleton height={16} width={80} />
                        </Tabs.Tab>
                        <Tabs.Tab color="dark" value="custom">
                          <Skeleton height={16} width={120} />
                        </Tabs.Tab>
                      </>
                    ) : (
                      <>
                        <Tabs.Tab color="dark" value="stock">
                          <AppText
                            style={{
                              fontSize: 14,
                              fontWeight:
                                filterState.activeTab === 'stock'
                                  ? 500
                                  : 'normal'
                            }}
                          >
                            Stock content
                          </AppText>
                        </Tabs.Tab>
                        {formState.customProducts.includeCustomProducts &&
                          formState.customProducts.imagePreview && (
                            <Tabs.Tab color="dark" value="custom">
                              <AppText
                                style={{
                                  fontSize: 14,
                                  fontWeight:
                                    filterState.activeTab === 'custom'
                                      ? 500
                                      : 'normal'
                                }}
                              >
                                Custom products
                              </AppText>
                            </Tabs.Tab>
                          )}
                      </>
                    )}
                  </Tabs.List>
                </Tabs>

                <TextInput
                  disabled={
                    loading ||
                    (stockContentProducts.length === 0 &&
                      filterState.activeTab === 'stock') ||
                    (customProducts.length === 0 &&
                      filterState.activeTab === 'custom')
                  }
                  onChange={(e) =>
                    setFilterState({
                      ...filterState,
                      search: e.currentTarget.value,
                      pageIndex: 1
                    })
                  }
                  placeholder="Search products by name"
                  size="sm"
                  style={{ marginTop: 5 }}
                  value={filterState.search}
                />
              </AppStack>
            </AppStack>

            <AppFlexbox
              style={{
                gap: 8,
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                Available products
              </AppText>
              {filteredProducts.length > 0 && (
                <AppCheckbox
                  checked={allProductsSelected}
                  label="Select all"
                  onChange={() => {
                    if (filterState.activeTab === 'custom') {
                      setFormState({
                        ...formState,
                        customProducts: {
                          ...formState.customProducts,
                          pkEcomExternalProducts: allProductsSelected
                            ? formState.customProducts.pkEcomExternalProducts.filter(
                                (id) =>
                                  !filteredProducts.some(
                                    (p) => p.pkEcomExternalProduct === id
                                  )
                              )
                            : [
                                ...formState.customProducts
                                  .pkEcomExternalProducts,
                                ...filteredProducts
                                  .map((p) => p.pkEcomExternalProduct)
                                  .filter(
                                    (id) =>
                                      !formState.customProducts.pkEcomExternalProducts.includes(
                                        id
                                      )
                                  )
                              ]
                        }
                      });
                    }
                    else {
                      setFormState({
                        ...formState,
                        pkEcomVendorProducts: allProductsSelected
                          ? formState.pkEcomVendorProducts.filter(
                              (id) =>
                                !filteredProducts.some(
                                  (p) => p.fkEcomVendorProduct === id
                                )
                            )
                          : [
                              ...formState.pkEcomVendorProducts,
                              ...filteredProducts
                                .map((p) => p.fkEcomVendorProduct)
                                .filter(
                                  (id) =>
                                    !formState.pkEcomVendorProducts.includes(id)
                                )
                            ]
                      });
                    }
                  }}
                />
              )}
            </AppFlexbox>

            <AppStack
              style={{
                flex: 1,
                borderRadius: 5,
                height: '100%',
                overflow: 'hidden'
              }}
            >
              {productsLoading ? (
                <AppStack style={{ gap: 2, border: 'solid 1px lightgrey' }}>
                  {Array(2)
                    .fill()
                    .map((_, s) => (
                      <AppCard
                        // eslint-disable-next-line react/no-array-index-key
                        key={s}
                        radius={8}
                        shadow="sm"
                        style={{ padding: 0 }}
                        withBorder
                      >
                        <AppFlexbox
                          style={{
                            minHeight: 40,
                            flex: 1,
                            padding: 12,
                            alignItems: 'center'
                          }}
                        >
                          <Skeleton height={30} radius={30} width={30} />
                          <AppStack style={{ flex: 1, gap: 2 }}>
                            <Skeleton height={10} mb={6} width="50%" />
                            <Skeleton height={10} width="40%" />
                          </AppStack>
                        </AppFlexbox>
                      </AppCard>
                    ))}
                </AppStack>
              ) : filteredProducts.length === 0 ? (
                <AppStack
                  style={{
                    padding: 20,
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    border: 'solid 1px lightgrey',
                    gap: 5
                  }}
                >
                  <AlertCircle color="#000" size={30} />
                  <AppText style={{ fontSize: 16, color: '#000' }}>
                    No products found
                  </AppText>

                  {filterState.search && (
                    <Button
                      color="dark"
                      onClick={() => {
                        setFilterState({
                          ...filterState,
                          search: '',
                          pageIndex: 1
                        });
                      }}
                      variant="outline"
                    >
                      Clear search
                    </Button>
                  )}
                </AppStack>
              ) : (
                <>
                  <PaginationList
                    items={filteredProducts
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((p) => {
                        const isSelected =
                          filterState.activeTab === 'custom'
                            ? formState.customProducts.pkEcomExternalProducts.includes(
                                p.pkEcomExternalProduct
                              )
                            : formState.pkEcomVendorProducts.includes(
                                p.fkEcomVendorProduct
                              );

                        return (
                          <AppFlexbox
                            key={
                              p.pkEcomExternalProduct || p.fkEcomVendorProduct
                            }
                            className={classes['hover-action-card']}
                            onClick={() => {
                              if (filterState.activeTab === 'custom') {
                                setFormState({
                                  ...formState,
                                  customProducts: {
                                    ...formState.customProducts,
                                    pkEcomExternalProducts: isSelected
                                      ? formState.customProducts.pkEcomExternalProducts.filter(
                                          (id) => id !== p.pkEcomExternalProduct
                                        )
                                      : [
                                          ...formState.customProducts
                                            .pkEcomExternalProducts,
                                          p.pkEcomExternalProduct
                                        ]
                                  }
                                });
                              }
                              else {
                                setFormState({
                                  ...formState,
                                  pkEcomVendorProducts: isSelected
                                    ? formState.pkEcomVendorProducts.filter(
                                        (id) => id !== p.fkEcomVendorProduct
                                      )
                                    : [
                                        ...formState.pkEcomVendorProducts,
                                        p.fkEcomVendorProduct
                                      ]
                                });
                              }
                            }}
                            style={{
                              padding: '10px 16px',
                              gap: 16,
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            <AppCheckbox
                              checked={isSelected}
                              onChange={() => {}}
                            />
                            <AppStack
                              style={{
                                width: 40,
                                height: 40,
                                border: p.previewImage
                                  ? 'none'
                                  : 'solid 1px #dee2e6',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {p.previewImage ? (
                                <Image h={40} src={p.previewImage} w={40} />
                              ) : (
                                <Photo color="#666" size={24} />
                              )}
                            </AppStack>
                            <AppStack style={{ gap: 3 }}>
                              <AppText
                                style={{ fontSize: 14, fontWeight: 500 }}
                              >
                                {p.name}
                              </AppText>
                            </AppStack>
                          </AppFlexbox>
                        );
                      })}
                    itemsPerPage={10}
                    onPageChange={(pageIndex) =>
                      setFilterState({
                        ...filterState,
                        pageIndex
                      })
                    }
                    pageIndex={filterState.pageIndex}
                  />

                  <AppText
                    style={{
                      textAlign: 'center',
                      fontSize: 14,
                      color: '#666',
                      marginTop: 10,
                      fontStyle: 'italic'
                    }}
                  >
                    You can always add more products later in the store
                    dashboard.
                  </AppText>
                </>
              )}
            </AppStack>
          </AppStack>
        </AppStack>
      )}
    </AppStack>
  );
};

StoreCreationSelectProductsStep.propTypes = {
  createOptions: PropTypes.object,
  formState: PropTypes.object,
  loading: PropTypes.bool,
  setFormState: PropTypes.func
};

export default StoreCreationSelectProductsStep;
